// src/components/ImportModal.js
import React, { useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Map old JSON fields → new client schema
function mapClient(old, ownerUid, ownerName) {
  const id = old.id || (Date.now().toString(36) + Math.random().toString(36).slice(2));

  // Map old platform fields - handle both old and new schema
  const platforms = (old.platforms || []).map(p => ({
    name: p.name || '',
    budget: p.budget || '',
    feePercent: p.feePercent || '',
    accountType: p.accountType || 'new',
    accessGranted: p.accessGranted || false,
    cid: p.cid || '',
    built: p.built || false,
    posted: p.posted || false,
    launched: p.launched || false,
    live: p.live || false,
    auditDone: p.auditDone || false,
    deliverables: p.deliverables || {},
    lpDeliverables: p.lpDeliverables || {},
    lpDelivEnabled: p.lpDelivEnabled !== false,
    lpSectionOpen: p.lpSectionOpen !== false,
  }));

  return {
    id,
    ownerUid,
    ownerName,
    name: old.name || '',
    cids: old.cids || '',
    vertical: old.vertical || '',
    verticals: old.verticals || (old.vertical ? old.vertical.split(',').map(v => v.trim()).filter(Boolean) : []),
    adsDestination: old.adsDestination || '',
    csm: old.csm || '',
    strategist: old.strategist || '',
    phase: old.phase || 'access',
    // Links - handle old field names
    wrinkleLink: old.project || old.wrike || old.wrinkleLink || '',
    analysisLink: old.analysis || old.analysisLink || '',
    salesforceLink: old.salesforce || old.salesforceLink || '',
    lpBuildTemplateLink: old.lpBuildTemplate || old.lpBuildTemplateLink || '',
    websiteUrl: old.websiteUrl || '',
    mrrAmount: old.mrrAmount || '',
    accountPoc: old.accountPoc || '',
    pocEmail: old.pocEmail || '',
    valueProps: old.valueProps || '',
    couponsPromos: old.couponsPromos || '',
    notes: old.notes || '',
    pinNote: old.pinNote || '',
    kickoff: old.kickoff || '',
    projLaunch: old.projLaunch || '',
    actualLaunch: old.actualLaunch || '',
    lastChecked: old.lastChecked || '',
    onHold: old.onHold || false,
    important: old.important || false,
    opspm: old.opspm || false,
    pmNotes: old.pmNotes || '',
    lpEnabled: old.lpEnabled || false,
    lpBuildOpen: old.lpBuildOpen !== false,
    platforms,
    lpSteps: old.lpSteps || {},
    trackSteps: old.trackSteps || {},
    pmSteps: old.pmSteps || {},
    adScheduleNotes: old.adScheduleNotes || '',
    serviceAreaNotes: old.serviceAreaNotes || '',
    phoneNumbers: old.phoneNumbers || '',
    campaignStructure: old.campaignStructure || '',
    adsWebsiteUrls: old.adsWebsiteUrls || '',
    adsLpUrls: old.adsLpUrls || '',
    createdAt: old.createdAt || Date.now(),
    updatedAt: Date.now(),
  };
}

export default function ImportModal({ ownerUid, ownerName, onClose, onDone }) {
  const [step, setStep] = useState('pick'); // pick | preview | importing | done
  const [clients, setClients] = useState([]);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        const raw = data.clients || (Array.isArray(data) ? data : []);
        if (!raw.length) { setError('No clients found in this file.'); return; }
        const mapped = raw.map(c => mapClient(c, ownerUid, ownerName));
        setClients(mapped);
        setStep('preview');
      } catch (err) {
        setError('Could not read file. Make sure it is the exported JSON from your old tracker.');
      }
    };
    reader.readAsText(file);
  }

  async function doImport() {
    setStep('importing');
    setProgress(0);
    for (let i = 0; i < clients.length; i++) {
      const c = clients[i];
      await setDoc(doc(db, 'clients', c.id), c, { merge: true });
      setProgress(i + 1);
    }
    setStep('done');
    setTimeout(() => { onDone(); onClose(); }, 1800);
  }

  const PHASE_LABELS = {
    access: 'Access & Deliverables', analysis: 'Analysis', building: 'Building',
    built: 'Built (waiting)', tracking: 'Tracking', qa: 'Q/A',
    prelaunch: 'Prelaunch', postlaunch: 'Postlaunch',
  };

  const PLAT_COLORS = {
    'Google Ads': '#185FA5', Microsoft: '#0F6E56', LSA: '#27500A',
    Meta: '#3C3489', Yelp: '#712B13', Nextdoor: '#633806',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 400, padding: 20,
      fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    }} onClick={e => { if (e.target === e.currentTarget && step !== 'importing') onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 640,
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        border: '1px solid rgba(0,0,0,0.15)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.1)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ fontSize: 20 }}>📂</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Import Existing Tracker</div>
            <div style={{ fontSize: 12, color: '#888' }}>Upload your exported JSON file to bring all clients in</div>
          </div>
          {step !== 'importing' && (
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#aaa' }}>✕</button>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

          {/* STEP: Pick file */}
          {step === 'pick' && (
            <div style={{ textAlign: 'center', padding: '30px 20px' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📁</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Select your exported tracker file</div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>
                This should be a <code style={{ background: '#f5f5f3', padding: '1px 5px', borderRadius: 4 }}>.json</code> file exported from your old paid media onboarding tracker.
              </div>
              <label style={{
                display: 'inline-block', padding: '10px 22px',
                background: '#185FA5', color: '#fff',
                borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700,
              }}>
                Choose File
                <input type="file" accept=".json" onChange={handleFile} style={{ display: 'none' }} />
              </label>
              {error && (
                <div style={{ marginTop: 16, color: '#A32D2D', fontSize: 13, background: '#FCEBEB', padding: '10px 14px', borderRadius: 8 }}>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* STEP: Preview */}
          {step === 'preview' && (
            <div>
              <div style={{
                background: '#EAF3DE', border: '1px solid #b8dfa0', borderRadius: 8,
                padding: '10px 14px', marginBottom: 16, fontSize: 13,
              }}>
                ✅ Found <strong>{clients.length} clients</strong> in <strong>{fileName}</strong> — ready to import
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>
                Preview of clients to import:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {clients.map((c, i) => {
                  const total = (c.platforms || []).reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: 8,
                      background: '#f8f8f8', border: '1px solid rgba(0,0,0,0.08)',
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{c.name}</div>
                        {c.pinNote && <div style={{ fontSize: 11, color: '#854F0B' }}>📌 {c.pinNote}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {(c.platforms || []).map((p, j) => (
                          <span key={j} style={{
                            fontSize: 10, padding: '2px 5px', borderRadius: 3,
                            fontWeight: 700, background: '#eee',
                            color: PLAT_COLORS[p.name] || '#333',
                          }}>{p.name}</span>
                        ))}
                      </div>
                      <span style={{
                        fontSize: 10, padding: '2px 7px', borderRadius: 10,
                        background: '#E6F1FB', color: '#185FA5', fontWeight: 700, whiteSpace: 'nowrap',
                      }}>
                        {PHASE_LABELS[c.phase] || c.phase}
                      </span>
                      {total > 0 && (
                        <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                          ${total.toLocaleString()}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP: Importing */}
          {step === 'importing' && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 36, marginBottom: 16 }}>⏳</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
                Importing {progress} of {clients.length} clients…
              </div>
              <div style={{ height: 10, background: '#f0f0f0', borderRadius: 5, overflow: 'hidden', maxWidth: 300, margin: '0 auto' }}>
                <div style={{
                  height: '100%', background: '#185FA5', borderRadius: 5,
                  width: `${Math.round((progress / clients.length) * 100)}%`,
                  transition: 'width 0.3s',
                }} />
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 10 }}>
                {Math.round((progress / clients.length) * 100)}% complete — do not close this window
              </div>
            </div>
          )}

          {/* STEP: Done */}
          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
              <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 8 }}>All {clients.length} clients imported!</div>
              <div style={{ fontSize: 13, color: '#888' }}>Your tracker is loading now…</div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'preview' && (
          <div style={{
            padding: '12px 20px', borderTop: '1px solid rgba(0,0,0,0.1)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div style={{ fontSize: 12, color: '#888' }}>
              Existing clients with matching IDs will be updated, not duplicated.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={{
                fontSize: 12, padding: '7px 14px', borderRadius: 7,
                background: '#f5f5f3', border: '1px solid rgba(0,0,0,0.18)',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>Cancel</button>
              <button onClick={doImport} style={{
                fontSize: 12, padding: '7px 18px', borderRadius: 7,
                background: '#185FA5', color: '#fff', border: 'none',
                cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
              }}>
                ⬆ Import {clients.length} Clients
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
